import React, {useState, useEffect} from 'react';
import {View, StyleSheet, TouchableOpacity} from 'react-native';
import {Modal, Portal, Text, TextInput, Button, RadioButton} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useInventoryCatalogStore} from '../../store/useInventoryCatalogStore';
import {useOrgUnitStore} from '../../store/useOrgUnitStore';
import DateTimePicker from '@react-native-community/datetimepicker';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {colors, spacing} from '../../theme';

const AddStockModal = ({visible, item, defaultOrgUnitId, onDismiss, onSuccess}) => {
  const {t} = useTranslation();
  const {createPurchaseOrder} = useInventoryCatalogStore();
  const {orgUnits, fetchOrgUnits} = useOrgUnitStore();
  const [quantity, setQuantity] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [orgUnitId, setOrgUnitId] = useState(defaultOrgUnitId || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (orgUnits.length === 0) {
      fetchOrgUnits();
    }
  }, [orgUnits.length, fetchOrgUnits]);

  useEffect(() => {
    if (visible) {
      setOrgUnitId(defaultOrgUnitId || (orgUnits.length === 1 ? orgUnits[0].id : null));
    }
  }, [visible, defaultOrgUnitId, orgUnits]);

  const handleSave = async () => {
    if (!quantity || !unitPrice) {
      setError(t('validation.required'));
      return;
    }
    if (!orgUnitId) {
      setError(t('inventory.storeRequired'));
      return;
    }

    setLoading(true);
    setError('');
    try {
      await createPurchaseOrder({
        orgUnitId,
        lines: [{
          catalogItemId: item.id,
          quantity: parseInt(quantity, 10),
          unitPrice: parseFloat(unitPrice),
        }],
        purchaseDate: purchaseDate.toISOString().split('T')[0],
      });
      setQuantity('');
      setUnitPrice('');
      setPurchaseDate(new Date());
      onSuccess();
    } catch (e) {
      setError(e.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const formatDisplay = (date) => date.toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'});

  if (!item) return null;

  const selectedStoreName = orgUnitId
    ? orgUnits.find(o => o.id === orgUnitId)?.name || 'Store'
    : t('inventory.selectStore');

  return (
    <Portal>
      <Modal visible={visible} onDismiss={onDismiss} contentContainerStyle={styles.modal}>
        <Text style={styles.title}>{t('inventory.addStock')}</Text>

        {/* Store Selection */}
        {orgUnits.length > 1 && (
          <View style={styles.storeSection}>
            <Text style={styles.storeLabel}>{t('inventory.store')} *</Text>
            <View style={styles.storeOptions}>
              {orgUnits.map(store => (
                <TouchableOpacity
                  key={store.id}
                  style={[styles.storeChip, orgUnitId === store.id && styles.storeChipSelected]}
                  onPress={() => setOrgUnitId(store.id)}>
                  <RadioButton.Android
                    value={String(store.id)}
                    status={orgUnitId === store.id ? 'checked' : 'unchecked'}
                    onPress={() => setOrgUnitId(store.id)}
                    color={colors.primary}
                  />
                  <Text style={[styles.storeChipText, orgUnitId === store.id && styles.storeChipTextSelected]}>
                    {store.name}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        )}

        <TextInput
          label={t('inventory.itemName')}
          value={item.name}
          disabled
          mode="outlined"
          style={styles.input}
        />

        {item.sku && (
          <TextInput
            label={t('inventory.sku')}
            value={item.sku}
            disabled
            mode="outlined"
            style={styles.input}
          />
        )}

        <TextInput
          label={t('inventory.purchaseQty')}
          value={quantity}
          onChangeText={setQuantity}
          keyboardType="decimal-pad"
          mode="outlined"
          style={styles.input}
        />

        <TextInput
          label={t('inventory.purchaseUnitPrice')}
          value={unitPrice}
          onChangeText={setUnitPrice}
          keyboardType="decimal-pad"
          mode="outlined"
          left={<TextInput.Affix text="₹" />}
          style={styles.input}
        />

        <Button
          mode="outlined"
          onPress={() => setShowDatePicker(true)}
          style={styles.dateButton}>
          {t('inventory.purchaseDate')}: {formatDisplay(purchaseDate)}
        </Button>

        {showDatePicker && (
          <DateTimePicker
            value={purchaseDate}
            mode="date"
            onChange={(e, date) => {
              setShowDatePicker(false);
              if (date) setPurchaseDate(date);
            }}
          />
        )}

        {error ? <Text style={styles.error}>{error}</Text> : null}

        <View style={styles.actions}>
          <Button mode="outlined" onPress={onDismiss} style={styles.actionBtn}>
            {t('common.cancel')}
          </Button>
          <Button mode="contained" onPress={handleSave} loading={loading} style={styles.actionBtn}>
            {t('common.save')}
          </Button>
        </View>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  modal: {
    backgroundColor: colors.surface,
    margin: spacing.md,
    padding: spacing.md,
    borderRadius: 15,
  },
  title: {fontSize: 18, fontWeight: '600', marginBottom: spacing.md, color: colors.text},
  storeSection: {marginBottom: spacing.md},
  storeLabel: {fontSize: 14, fontWeight: '600', color: colors.text, marginBottom: spacing.xs},
  storeOptions: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs},
  storeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingRight: spacing.sm,
    marginBottom: spacing.xs,
  },
  storeChipSelected: {
    borderColor: colors.primary,
    backgroundColor: colors.primary + '10',
  },
  storeChipText: {fontSize: 13, color: colors.text},
  storeChipTextSelected: {color: colors.primary, fontWeight: '500'},
  input: {marginBottom: spacing.sm},
  dateButton: {marginBottom: spacing.sm},
  error: {color: colors.error, marginBottom: spacing.sm, fontSize: 13},
  actions: {flexDirection: 'row', justifyContent: 'flex-end', gap: spacing.sm, marginTop: spacing.sm},
  actionBtn: {minWidth: 100, borderRadius: 10, paddingHorizontal: 10},
});

export default AddStockModal;
